import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCw, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { StatusBadge } from "../StatusTimeline";
import { Empty, Stat } from "../ProtectedRoute";
import { TicketThread } from "../TicketThread";
import { useCountry } from "../../context/CountryContext";
import { api, errMsg, money, fmtDate, flag, TECH, STATUS_LABEL } from "../../lib/api";

const useList = (url) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(url).then((r) => setRows(r.data)).catch(() => {}), [url]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
};

const act = async (fn, msg) => {
  try {
    await fn();
    if (msg) toast.success(msg);
  } catch (e) {
    toast.error(errMsg(e));
  }
};

export const OverviewTab = () => {
  const [s, load] = useList("/admin/stats");
  if (!s) return null;
  const fmt = (o) => Object.entries(o).map(([c, v]) => money(v, c)).join(" · ") || "—";
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Customers" value={s.customers} testid="admin-stat-customers" />
        <Stat label="Live suppliers" value={s.suppliers} testid="admin-stat-suppliers" />
        <Stat label="Suppliers pending" value={s.suppliers_pending} tone="text-primary" testid="admin-stat-suppliers-pending" />
        <Stat label="Packages pending" value={s.packages_pending} tone="text-primary" testid="admin-stat-packages-pending" />
        <Stat label="Orders (paid)" value={`${s.orders_paid}/${s.orders}`} testid="admin-stat-orders" />
        <Stat label="Waiting on supplier" value={s.services_waiting} testid="admin-stat-waiting" />
        <Stat label="Open tickets" value={s.tickets_open} testid="admin-stat-tickets" />
        <Stat label="Countries enabled" value={`${s.countries_enabled}/54`} testid="admin-stat-countries" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border bg-white p-5"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monthly recurring revenue</p><p className="mt-2 font-display text-xl font-bold" data-testid="admin-mrr">{fmt(s.monthly_revenue)}</p></div>
        <div className="rounded-lg border bg-white p-5"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monthly margin</p><p className="mt-2 font-display text-xl font-bold text-secondary" data-testid="admin-margin">{fmt(s.monthly_margin)}</p></div>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-white p-5">
        <p className="flex-1 text-sm">Bill services that are due for renewal. Card customers are auto-charged; mobile money customers get a Pay now invoice.</p>
        {[[0, "Run billing", "admin-run-billing-btn", "default"], [30, "Bill next 30 days", "admin-run-billing-30-btn", "outline"]].map(([days, label, tid, variant]) => (
          <Button key={tid} variant={variant} data-testid={tid} onClick={() => act(async () => { const { data } = await api.post(`/admin/billing/run?days_ahead=${days}`); toast.success(`${data.created} renewal invoices created (due by ${data.cutoff})`); load(); })}>{label}</Button>
        ))}
      </div>
    </div>
  );
};

export const SuppliersTab = () => {
  const [rows, load] = useList("/admin/suppliers");
  if (!rows) return null;
  const patch = (id, body, msg) => act(() => api.patch(`/admin/suppliers/${id}`, body).then(load), msg);
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <Table data-testid="admin-supplier-approval-table">
        <TableHeader><TableRow><TableHead>Supplier</TableHead><TableHead>Countries</TableHead><TableHead>Integration</TableHead><TableHead>Packages</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>{rows.map((s) => (
          <TableRow key={s.id} data-testid={`admin-supplier-row-${s.id}`}>
            <TableCell><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.contact_email}</p></TableCell>
            <TableCell className="text-xs">{s.countries.join(", ")}</TableCell>
            <TableCell>
              <Select value={s.integration} onValueChange={(v) => patch(s.id, { integration: v, ...(v === "api" ? { connector: "afrinet" } : {}) }, "Integration updated")}>
                <SelectTrigger className="h-8 w-36" data-testid={`admin-supplier-integration-${s.id}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="portal">Portal</SelectItem><SelectItem value="api">API ({s.connector || "afrinet"})</SelectItem></SelectContent>
              </Select>
            </TableCell>
            <TableCell>{s.package_count}</TableCell>
            <TableCell><StatusBadge status={s.status} /></TableCell>
            <TableCell className="whitespace-nowrap">
              {s.status !== "approved" && <Button size="sm" onClick={() => patch(s.id, { status: "approved" }, `${s.name} approved`)} data-testid={`admin-approve-supplier-${s.id}`}>Approve</Button>}
              {s.status !== "rejected" && <Button size="sm" variant="ghost" onClick={() => patch(s.id, { status: s.status === "approved" ? "suspended" : "rejected" }, "Updated")} data-testid={`admin-reject-supplier-${s.id}`}>{s.status === "approved" ? "Suspend" : "Reject"}</Button>}
            </TableCell>
          </TableRow>))}
        </TableBody>
      </Table>
    </div>
  );
};

const PriceCell = ({ p, ccy, onSave }) => {
  const [v, setV] = useState(p.retail_price ?? "");
  useEffect(() => setV(p.retail_price ?? ""), [p.retail_price]);
  const margin = v ? Math.round(((v - p.wholesale_price) / p.wholesale_price) * 100) : null;
  return (
    <div className="flex items-center gap-2">
      <Input type="number" className="h-8 w-28" value={v} onChange={(e) => setV(e.target.value)} placeholder="auto +markup" data-testid={`admin-retail-input-${p.id}`} />
      <Button size="sm" variant="outline" disabled={!v || +v === p.retail_price} onClick={() => onSave(+v)} data-testid={`admin-retail-save-${p.id}`}>Save</Button>
      {margin != null && <span className={`text-xs ${margin < 0 ? "text-red-600" : "text-secondary"}`}>{margin}%</span>}
    </div>
  );
};

export const PackagesTab = () => {
  const { countries } = useCountry();
  const [status, setStatus] = useState("all");
  const [rows, load] = useList(`/admin/packages${status === "all" ? "" : `?status=${status}`}`);
  const ccy = (c) => countries.find((x) => x.code === c)?.currency || "USD";
  const patch = (id, body, msg) => act(() => api.patch(`/admin/packages/${id}`, body).then(load), msg);
  return (
    <div>
      <div className="mb-4 flex gap-2">{["all", "pending", "approved", "rejected"].map((s) => (
        <Button key={s} size="sm" variant={status === s ? "default" : "outline"} onClick={() => setStatus(s)} data-testid={`admin-pkg-filter-${s}`}>{s === "all" ? "All" : STATUS_LABEL[s]}</Button>))}
      </div>
      {rows && (rows.length ? (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <Table data-testid="admin-packages-table">
            <TableHeader><TableRow><TableHead>Package</TableHead><TableHead>Supplier</TableHead><TableHead>Country</TableHead><TableHead>Wholesale</TableHead><TableHead>Retail price</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{rows.map((p) => (
              <TableRow key={p.id} data-testid={`admin-pkg-row-${p.id}`}>
                <TableCell><p className="font-medium">{p.name}</p><p className="text-xs text-muted-foreground">{TECH[p.type]?.label} · {p.download_mbps}Mbps · {p.data_cap}</p></TableCell>
                <TableCell className="text-sm">{p.supplier_name}</TableCell>
                <TableCell>{p.country}</TableCell>
                <TableCell>{money(p.wholesale_price, ccy(p.country))}</TableCell>
                <TableCell><PriceCell p={p} ccy={ccy(p.country)} onSave={(v) => patch(p.id, { retail_price: v }, "Retail price saved")} /></TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  {p.status !== "approved" && <Button size="sm" onClick={() => patch(p.id, { status: "approved" }, "Package approved")} data-testid={`admin-approve-pkg-${p.id}`}>Approve</Button>}
                  {p.status !== "rejected" && <Button size="sm" variant="ghost" onClick={() => patch(p.id, { status: "rejected" }, "Package rejected")} data-testid={`admin-reject-pkg-${p.id}`}>Reject</Button>}
                </TableCell>
              </TableRow>))}
            </TableBody>
          </Table>
        </div>) : <Empty>No packages.</Empty>)}
    </div>
  );
};

export const CountriesTab = () => {
  const { countries, reload } = useCountry();
  const [q, setQ] = useState("");
  const [markup, setMarkup] = useState({});
  const toggle = (c) => act(() => api.patch(`/admin/countries/${c.code}`, { enabled: !c.enabled }).then(reload), `${c.name} ${c.enabled ? "disabled" : "enabled"}`);
  const apply = (c) => act(async () => { const { data } = await api.post("/admin/pricing/apply-markup", { country: c.code, markup: +markup[c.code] }); toast.success(`${data.updated} prices updated in ${c.name}`); reload(); });
  return (
    <div>
      <div className="relative mb-4 max-w-xs"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-country-search" /></div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-country-toggle-grid">
        {countries.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).map((c) => (
          <div key={c.code} className="rounded-lg border bg-white p-4" data-testid={`admin-country-${c.code}`}>
            <div className="flex items-center gap-2">
              <img src={flag(c.code)} alt="" className="h-3.5 w-5 rounded-[2px]" loading="lazy" />
              <span className="flex-1 truncate font-medium">{c.name}</span>
              <span className="font-mono-data text-xs text-muted-foreground">{c.currency}</span>
              <Switch checked={c.enabled} onCheckedChange={() => toggle(c)} data-testid={`admin-country-switch-${c.code}`} />
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex-1">{c.package_count} packages · {c.live ? "Live" : "Coming soon"}{!c.flw_supported && " · USD card"}</span>
              <Input className="h-7 w-16 text-xs" type="number" placeholder="+%" value={markup[c.code] ?? ""} onChange={(e) => setMarkup({ ...markup, [c.code]: e.target.value })} data-testid={`admin-markup-input-${c.code}`} />
              <Button size="sm" variant="outline" className="h-7" disabled={!markup[c.code]} onClick={() => apply(c)} data-testid={`admin-markup-apply-${c.code}`}>Apply</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const OrdersTab = () => {
  const [rows, load] = useList("/admin/services");
  const [orders] = useList("/admin/orders");
  const patch = (id, status) => act(() => api.patch(`/admin/services/${id}`, { status }).then(load), `Status set to ${STATUS_LABEL[status]}`);
  const retry = (id) => act(() => api.post(`/admin/services/${id}/retry`).then(load), "Fulfilment retried");
  return (
    <div className="space-y-6">
      <div className="overflow-x-auto rounded-lg border bg-white">
        <p className="border-b p-4 font-semibold">Services & fulfilment</p>
        {rows && rows.length ? (
          <Table data-testid="admin-services-table">
            <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Customer</TableHead><TableHead>Supplier</TableHead><TableHead>Route</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{rows.map((s) => (
              <TableRow key={s.id} data-testid={`admin-service-row-${s.id}`}>
                <TableCell><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.country} · {money(s.price, s.currency)}/mo · #{s.order_number}</p></TableCell>
                <TableCell className="text-sm">{s.user_name}</TableCell>
                <TableCell className="text-sm">{s.supplier_name}</TableCell>
                <TableCell className="text-xs">{s.fulfilment === "api" ? `API ${s.ext_ref || ""}` : "Portal"}</TableCell>
                <TableCell>
                  <Select value={s.status} onValueChange={(v) => patch(s.id, v)}>
                    <SelectTrigger className="h-8 w-44" data-testid={`admin-service-status-${s.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["pending_supplier", "accepted", "installation_scheduled", "active", "suspended", "rejected", "cancelled", "failed"].map((k) => <SelectItem key={k} value={k}>{STATUS_LABEL[k]}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{["pending_supplier", "failed"].includes(s.status) && <Button size="sm" variant="ghost" onClick={() => retry(s.id)} data-testid={`admin-service-retry-${s.id}`}><RotateCw className="mr-1 h-3.5 w-3.5" />Retry</Button>}</TableCell>
              </TableRow>))}
            </TableBody>
          </Table>) : <p className="p-6 text-sm text-muted-foreground">No services yet.</p>}
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <p className="border-b p-4 font-semibold">All orders</p>
        {orders && orders.length ? (
          <Table data-testid="admin-orders-table">
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{orders.map((o) => (
              <TableRow key={o.id}>
                <TableCell className="font-mono-data text-xs">{o.number}<br />{fmtDate(o.created_at)}</TableCell>
                <TableCell className="text-sm">{o.user_email}</TableCell>
                <TableCell className="max-w-xs text-xs">{o.items.map((i) => i.name).join(", ")}</TableCell>
                <TableCell>{money(o.total, o.currency)}</TableCell>
                <TableCell className="text-xs">{o.payment_method}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
              </TableRow>))}
            </TableBody>
          </Table>) : <p className="p-6 text-sm text-muted-foreground">No orders yet.</p>}
      </div>
    </div>
  );
};

export const CustomersTab = () => {
  const [rows] = useList("/admin/customers");
  if (!rows) return null;
  return rows.length ? (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <Table data-testid="admin-customers-table">
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Country</TableHead><TableHead>Services</TableHead><TableHead>Domains</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((c) => (
          <TableRow key={c.id}><TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.email}</TableCell><TableCell>{c.country}</TableCell><TableCell>{c.services}</TableCell><TableCell>{c.domains}</TableCell><TableCell>{fmtDate(c.created_at)}</TableCell></TableRow>))}
        </TableBody>
      </Table>
    </div>) : <Empty>No customers yet.</Empty>;
};

export const TicketsTab = () => {
  const [rows, load] = useList("/admin/tickets");
  if (!rows) return null;
  return rows.length ? (
    <div className="space-y-4" data-testid="admin-tickets-list">
      {rows.map((t) => (
        <TicketThread key={t.id} ticket={t} onUpdate={load} extra={<>
          <span className="text-xs text-muted-foreground">{t.user_email}</span>
          {t.status !== "closed" && <Button size="sm" variant="outline" onClick={() => act(() => api.patch(`/admin/tickets/${t.id}`, { status: "closed" }).then(load), "Ticket closed")} data-testid={`admin-close-ticket-${t.id}`}>Close</Button>}
        </>} />
      ))}
    </div>) : <Empty>No tickets.</Empty>;
};

export const SettingsTab = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = () => act(() => api.put("/admin/settings", { default_markup: +s.default_markup, free_domain_enabled: s.free_domain_enabled, fallback_tld: s.fallback_tld }), "Settings saved");
  return (
    <div className="max-w-xl space-y-5 rounded-lg border bg-white p-6" data-testid="admin-settings-form">
      <div><Label>Default markup on wholesale (%)</Label><Input className="mt-1" type="number" value={s.default_markup} onChange={(e) => setS({ ...s, default_markup: e.target.value })} data-testid="settings-markup-input" /></div>
      <div className="flex items-center justify-between rounded-md border p-3">
        <div><p className="text-sm font-semibold">Free domain with internet packages</p><p className="text-xs text-muted-foreground">Country-code domain where the registrar supports it, otherwise the fallback below. First year only.</p></div>
        <Switch checked={s.free_domain_enabled} onCheckedChange={(v) => setS({ ...s, free_domain_enabled: v })} data-testid="settings-free-domain-switch" />
      </div>
      <div><Label>Fallback free extension</Label><Input className="mt-1 font-mono-data" value={s.fallback_tld} onChange={(e) => setS({ ...s, fallback_tld: e.target.value })} data-testid="settings-fallback-tld-input" /></div>
      <Button onClick={save} data-testid="settings-save-btn">Save settings</Button>
    </div>
  );
};

export const ConnectorsTab = () => {
  const [rows] = useList("/admin/connectors");
  if (!rows) return null;
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="admin-connectors-list">
      {rows.map((c) => (
        <div key={c.key} className="rounded-lg border bg-white p-5" data-testid={`connector-${c.key}`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.type}</p>
          <p className="mt-1 font-display text-lg font-bold">{c.name}</p>
          <span className={`mt-3 inline-block rounded px-2 py-0.5 text-xs font-bold ${c.mode === "live" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{c.mode.toUpperCase()}</span>
          {c.mode !== "live" && <p className="mt-2 text-xs text-muted-foreground">Goes live when <span className="font-mono-data">{c.env_key}</span> is added.</p>}
        </div>
      ))}
    </div>
  );
};
